import { Heart, ExternalLink, ShoppingBag, Leaf } from 'lucide-react';
import { useWishlist } from '@/context/WishlistContext';
import { useAuth } from '@/context/AuthContext';
import { useRegion } from '@/context/RegionContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { useSiteCategories } from '@/context/SiteCategoriesContext';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { resolveImageUrl } from '@/lib/api';
import { generateSlug, getProductCanonicalUrl } from '@/lib/formatters';
import { usePrefetchProduct } from '@/hooks/useApiQueries';
import BlurImage from '@/components/BlurImage';
import { saveScrollPosition } from '@/components/common/ScrollManager';

// Shared pill styles
const PILL_STYLES = "text-[10px] px-2 py-0.5 bg-site-tag-bg text-site-tag-text rounded-full border border-site-tag-border whitespace-nowrap flex-shrink-0";
const SMALL_PILL_STYLES = "text-[8px] px-1.5 py-0.5 bg-site-tag-bg text-site-tag-text rounded-full border border-site-tag-border normal-case tracking-normal";

// Pure function to calculate how many pills fit in given width
// NOTE: Badge is displayed in header row, not in pills row, so no badge space reservation needed
const calculateFittingPills = (pillWidths, containerWidth, gapSize = 4) => {
  if (!pillWidths.length || containerWidth <= 0) return pillWidths.length || 1;
  
  let totalWidth = 0;
  let count = 0;
  
  for (let i = 0; i < pillWidths.length; i++) {
    const pillWidth = pillWidths[i];
    if (pillWidth <= 0) continue;
    const neededWidth = count > 0 ? pillWidth + gapSize : pillWidth;
    if (totalWidth + neededWidth <= containerWidth) {
      totalWidth += neededWidth;
      count++;
    } else {
      break;
    }
  }
  
  return Math.max(count, 1);
};

const ProductGroupCard = ({ group, variant = 'default' }) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { isAuthenticated } = useAuth();
  const { region } = useRegion();
  const { isShopifyEnabled } = useSiteSettings();
  const { getCategoryTitle, getSubcategoryTitle, getCategory } = useSiteCategories();
  const location = useLocation();
  const cardRef = useRef(null);
  const tagsContainerRef = useRef(null);
  const pillsContainerRef = useRef(null);
  const pillWidthsRef = useRef([]); // Cache measured pill widths
  const containerWidthRef = useRef(0); // Cache container width
  const [visibleTagCount, setVisibleTagCount] = useState(999);
  const [visiblePillCount, setVisiblePillCount] = useState(999); // Start high so all pills render for measurement
  const prefetchProduct = usePrefetchProduct();
  
  // Memoized values - only show as in wishlist if user is authenticated
  const inWishlist = isAuthenticated && isInWishlist(group.id);
  const categoryTitle = useMemo(() => getCategoryTitle(group.category), [getCategoryTitle, group.category]);
  const subcategoryTitle = useMemo(() => getSubcategoryTitle(group.category, group.subcategory), [getSubcategoryTitle, group.category, group.subcategory]);
  const cardHoverText = useMemo(() => {
    const category = getCategory(group.category);
    return category?.card_hover_text || 'View Look';
  }, [getCategory, group.category]);
  const affiliateLink = useMemo(() => {
    if (region === 'us' && group.affiliate_link_us) return group.affiliate_link_us;
    if (region === 'es' && group.affiliate_link_es) return group.affiliate_link_es;
    return group.affiliate_link_uk;
  }, [region, group.affiliate_link_us, group.affiliate_link_es, group.affiliate_link_uk]);
  const slug = useMemo(() => group.slug || generateSlug(group.title), [group.slug, group.title]);
  const canonicalUrl = useMemo(() => getProductCanonicalUrl(group), [group]);
  
  // Determine sales mode - default to affiliate if not set
  const salesMode = group.sales_mode || 'affiliate';
  const isDropshipMode = salesMode === 'dropship';
  const isAffiliateMode = salesMode === 'affiliate';
  
  // Check if product has Shopify link (only in dropship mode and Shopify is enabled)
  const shopifyProductId = group.shopify_product?.product_id || group.shopify_product?.variant_id;
  const hasShopifyLink = useMemo(() => 
    isDropshipMode && isShopifyEnabled && shopifyProductId && group.shopify_product?.available !== false,
    [isDropshipMode, isShopifyEnabled, shopifyProductId, group.shopify_product?.available]
  );
  
  // Filter products by region - memoized
  // For favorites variant, show all products regardless of region
  // For dropship mode, show all named products (no Amazon URL required)
  const visibleProducts = useMemo(() => {
    if (isDropshipMode) {
      // In dropship mode, show all products with names
      return (group.products || []).filter(p => p.name);
    }
    if (variant === 'favorites') {
      // Show all products that have at least one Amazon URL
      return (group.products || []).filter(p => 
        p.amazon_url_us || p.amazon_url_uk || p.amazon_url_es
      );
    }
    // Affiliate mode - filter by region
    return (group.products || []).filter(p => {
      if (region === 'us') return p.amazon_url_us;
      if (region === 'es') return p.amazon_url_es;
      return p.amazon_url_uk;
    });
  }, [group.products, region, variant, isDropshipMode]);
  
  const hiddenCount = visibleProducts.length > visiblePillCount ? visibleProducts.length - visiblePillCount : 0;

  // Save scroll position when clicking to product detail page
  const handleProductClick = useCallback(() => {
    // Save current scroll position for this page path
    saveScrollPosition(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Prefetch product data on hover
  const handleMouseEnter = useCallback(() => {
    if (slug) {
      prefetchProduct(slug);
    }
  }, [slug, prefetchProduct]);

  // Cache for tag widths
  const tagWidthsRef = useRef([]);
  const tagContainerWidthRef = useRef(0);

  // Calculate visible tags - reserve space for "+X" badge if not all fit
  useEffect(() => {
    if (!group.group_tags?.length || !cardRef.current || !tagsContainerRef.current) return;
    
    const calculateTags = () => {
      const container = tagsContainerRef.current;
      if (!container) return;
      
      const containerWidth = container.offsetWidth;
      if (containerWidth === 0) return;
      
      // Measure all tag widths on first run or when tags change
      if (tagWidthsRef.current.length !== group.group_tags.length || tagContainerWidthRef.current !== containerWidth) {
        const tags = container.querySelectorAll('[data-tag]');
        // If some tags are hidden, we need to temporarily show them to measure
        const widths = [];
        tags.forEach((tag, idx) => {
          const wasHidden = tag.classList.contains('hidden');
          if (wasHidden) tag.classList.remove('hidden');
          widths.push(tag.getBoundingClientRect().width);
          if (wasHidden) tag.classList.add('hidden');
        });
        
        // If we couldn't measure (all hidden), estimate based on text length
        if (widths.every(w => w === 0)) {
          group.group_tags.forEach((tag, idx) => {
            widths[idx] = tag.length * 6 + 16; // rough estimate: 6px per char + padding
          });
        }
        
        tagWidthsRef.current = widths;
        tagContainerWidthRef.current = containerWidth;
      }
      
      const widths = tagWidthsRef.current;
      const gapSize = 6; // gap-1.5 = 6px
      const badgeWidth = 32; // "+X" badge width estimate
      
      // First pass: check if all tags fit without badge
      let totalWidthAll = 0;
      for (let i = 0; i < widths.length; i++) {
        totalWidthAll += i > 0 ? widths[i] + gapSize : widths[i];
      }
      
      // If all fit, show all
      if (totalWidthAll <= containerWidth) {
        setVisibleTagCount(group.group_tags.length);
        return;
      }
      
      // Second pass: reserve space for "+X" badge
      const availableWidth = containerWidth - badgeWidth - gapSize;
      let totalWidth = 0;
      let count = 0;
      
      for (let i = 0; i < widths.length; i++) {
        const neededWidth = count > 0 ? widths[i] + gapSize : widths[i];
        if (totalWidth + neededWidth <= availableWidth) {
          totalWidth += neededWidth;
          count++;
        } else {
          break;
        }
      }
      
      setVisibleTagCount(Math.max(count, 1));
    };

    // Initial calculation with slight delay
    const timer = setTimeout(calculateTags, 100);
    
    const resizeObserver = new ResizeObserver(() => {
      tagContainerWidthRef.current = 0; // Reset to force recalculation
      requestAnimationFrame(calculateTags);
    });
    
    resizeObserver.observe(cardRef.current);
    
    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [group.group_tags]);

  // Measure pill widths by rendering all pills initially, then calculating which fit
  // Phase 1: Start with all pills visible (high count)
  // Phase 2: After render, measure actual pill widths and container, then calculate
  useEffect(() => {
    if (!visibleProducts.length || !pillsContainerRef.current) return;
    
    let measurementDone = false;
    
    const measureAndCalculate = () => {
      const container = pillsContainerRef.current;
      if (!container) return;
      
      const containerWidth = container.offsetWidth;
      if (containerWidth === 0) return;
      
      // Get all rendered pills
      const pills = container.querySelectorAll('[data-pill]');
      if (pills.length === 0) return;
      
      // Measure widths of all visible pills
      const widths = Array.from(pills).map(pill => pill.getBoundingClientRect().width);
      
      // Check if we have valid measurements (not 0)
      const validWidths = widths.filter(w => w > 0);
      if (validWidths.length !== visibleProducts.length && !measurementDone) {
        // Not all pills are visible yet, wait a bit more
        return;
      }
      
      // Cache the measured widths
      if (validWidths.length === visibleProducts.length) {
        pillWidthsRef.current = widths;
        measurementDone = true;
      }
      
      // Calculate how many fit
      if (pillWidthsRef.current.length > 0) {
        const newCount = calculateFittingPills(pillWidthsRef.current, containerWidth);
        
        // Only update if count actually changed
        if (newCount !== visiblePillCount) {
          setVisiblePillCount(newCount);
        }
      }
      
      containerWidthRef.current = containerWidth;
    };
    
    // Initial measurement after a brief delay for fonts/styles to load
    const timer = setTimeout(measureAndCalculate, 100);
    
    // Re-measure on container resize
    const resizeObserver = new ResizeObserver(() => {
      // On resize, we need to recalculate with cached widths
      const container = pillsContainerRef.current;
      if (!container || pillWidthsRef.current.length === 0) return;
      
      const containerWidth = container.offsetWidth;
      if (containerWidth === 0 || containerWidth === containerWidthRef.current) return;
      
      containerWidthRef.current = containerWidth;
      const newCount = calculateFittingPills(pillWidthsRef.current, containerWidth);
      setVisiblePillCount(newCount);
    });
    
    resizeObserver.observe(pillsContainerRef.current);
    
    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [visibleProducts, visiblePillCount]);

  const handleWishlistClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const result = await toggleWishlist({ ...group, type: 'group' });
    
    if (result.requiresAuth) return;
    
    if (result.added) {
      toast.success('Added to wishlist');
    } else {
      toast('Removed from wishlist');
    }
  }, [group, toggleWishlist]);

  return (
    <div 
      ref={cardRef}
      className="product-card block relative group h-full flex flex-col"
      data-testid={`product-group-card-${group.id}`}
      onMouseEnter={handleMouseEnter}
    >
      {/* Badges - Top Left */}
      <div className="absolute top-3 left-3 z-10 flex gap-1.5">
        {group.is_new && !group.is_sold_out && (
          <span className="badge-new" data-testid={`badge-new-${group.id}`}>New</span>
        )}
      </div>

      {/* Wishlist Button - positioned above the link */}
      <button
        onClick={handleWishlistClick}
        className={`absolute top-3 right-3 z-20 p-2 transition-all duration-300 ${
          inWishlist 
            ? 'bg-[#FF3B30] text-white' 
            : 'bg-white/90 hover:bg-[#FF3B30] hover:text-white'
        }`}
        data-testid={`wishlist-toggle-${group.id}`}
      >
        <Heart className={`w-5 h-5 ${inWishlist ? 'fill-current' : ''}`} />
      </button>

      {/* Clickable area - Image + Info Section (everything above Shop button) */}
      <Link
        to={canonicalUrl}
        onClick={handleProductClick}
        className="block cursor-pointer flex-grow"
        data-testid={`group-card-link-${group.id}`}
      >
        {/* Image */}
        <div className="aspect-[4/3] overflow-hidden bg-site-bg-alt relative">
          <BlurImage
            src={resolveImageUrl(group.card_image_url || group.image_url) || 'https://via.placeholder.com/400x500'}
            alt={group.image_alt || group.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            containerClassName="w-full h-full"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white px-4 py-2 font-mono text-xs uppercase tracking-widest">
              {cardHoverText}
            </span>
          </div>
          {/* Sustainable Badge - Bottom Right of Image */}
          {group.is_sustainable && (
            <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-green-600 text-white text-[10px] font-mono uppercase px-2 py-1 rounded-sm" data-testid={`badge-sustainable-${group.id}`}>
              <Leaf className="w-3 h-3 flex-shrink-0" />
              <span className="leading-none translate-y-[0.5px]">Sustainable</span>
            </span>
          )}
        </div>

        {/* Info Section */}
        <div className="p-4 pt-2 pb-0 bg-white">
          {/* Category */}
          <div className="h-4 overflow-hidden">
            <p className="font-mono text-[10px] text-site-text-subtle uppercase tracking-wide truncate">
              {categoryTitle}{group.subcategory && ` · ${subcategoryTitle}`}
            </p>
          </div>
          
          {/* Title */}
          <h3 className="font-body text-sm font-medium truncate leading-tight" title={group.title}>
            {group.title}
          </h3>

          {/* Description */}
          <div className="h-11 flex items-center overflow-hidden">
            <p className="text-[11px] text-site-text-muted leading-relaxed line-clamp-2">
              {group.card_description || group.description || ' '}
            </p>
          </div>

          {/* Products List */}
          <div className="pt-1">
            <p className="font-mono text-[9px] uppercase tracking-widest text-site-text-muted mb-1 flex items-center gap-1 min-h-[18px]">
              What's in this look:
              {hiddenCount > 0 ? (
                <span className={SMALL_PILL_STYLES}>+{hiddenCount}</span>
              ) : (
                <span className="invisible text-[8px] px-1.5 py-0.5">+0</span>
              )}
            </p>
            <div className="h-7 flex items-center overflow-hidden">
              {visibleProducts.length > 0 ? (
                <div 
                  ref={pillsContainerRef} 
                  className="flex flex-nowrap gap-1 w-full items-center"
                >
                  {/* Render ALL pills for measurement, hide overflow via visibility */}
                  {visibleProducts.map((product, idx) => (
                    <span 
                      key={idx}
                      data-pill
                      className={PILL_STYLES}
                      style={{
                        visibility: idx < visiblePillCount ? 'visible' : 'hidden',
                        position: idx < visiblePillCount ? 'relative' : 'absolute'
                      }}
                      data-testid={`product-item-${group.id}-${idx}`}
                    >
                      {product.name}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-[10px] text-site-text-subtle italic">No items available for your region</span>
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* Bottom section - Not clickable to product page */}
      <div className="px-4 pb-0 bg-white mt-auto">
        {/* Select Options Button - Shopify dropship mode - redirects to product page */}
        {hasShopifyLink && variant !== 'favorites' && (
          group.is_sold_out ? (
            <div
              className="mt-2 w-full flex items-center justify-center gap-2 bg-gray-400 text-white py-2.5 px-4 font-mono text-[10px] uppercase tracking-widest cursor-not-allowed"
              data-testid={`sold-out-btn-${group.id}`}
            >
              Sold Out
            </div>
          ) : (
            <Link
              to={canonicalUrl}
              onClick={handleProductClick}
              className="mt-2 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2.5 px-4 font-mono text-[10px] uppercase tracking-widest hover:from-purple-700 hover:to-indigo-700 transition-all"
              data-testid={`select-options-${group.id}`}
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              Select Options
            </Link>
          )
        )}
        
        {/* Shop Button(s) - Only in affiliate mode or favorites variant */}
        {variant === 'favorites' ? (
          /* Multi-region Amazon buttons for Favorites page - only for affiliate mode products */
          group.is_sold_out ? (
            <div
              className="mt-2 w-full flex items-center justify-center gap-2 bg-gray-400 text-white py-2 px-2 font-mono text-[9px] uppercase tracking-wider cursor-not-allowed"
              data-testid={`sold-out-btn-fav-${group.id}`}
            >
              Sold Out
            </div>
          ) : isAffiliateMode ? (
            <div className="mt-2 flex gap-1">
              {group.affiliate_link_us && (
                <a
                  href={group.affiliate_link_us}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 bg-site-bg-dark text-site-text-inverse py-2 px-2 font-mono text-[9px] uppercase tracking-wider hover:bg-site-bg-dark/90 transition-colors"
                  data-testid={`shop-amazon-us-${group.id}`}
                >
                  <img src="https://flagcdn.com/w20/us.png" alt="US" className="w-4 h-3 object-cover" />
                  Amazon
                </a>
              )}
              {group.affiliate_link_uk && (
                <a
                  href={group.affiliate_link_uk}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 bg-site-bg-dark text-site-text-inverse py-2 px-2 font-mono text-[9px] uppercase tracking-wider hover:bg-site-bg-dark/90 transition-colors"
                  data-testid={`shop-amazon-uk-${group.id}`}
                >
                  <img src="https://flagcdn.com/w20/gb.png" alt="UK" className="w-4 h-3 object-cover" />
                  Amazon
                </a>
              )}
              {group.affiliate_link_es && (
                <a
                  href={group.affiliate_link_es}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 bg-site-bg-dark text-site-text-inverse py-2 px-2 font-mono text-[9px] uppercase tracking-wider hover:bg-site-bg-dark/90 transition-colors"
                  data-testid={`shop-amazon-es-${group.id}`}
                >
                  <img src="https://flagcdn.com/w20/es.png" alt="ES" className="w-4 h-3 object-cover" />
                  Amazon
                </a>
              )}
            </div>
          ) : hasShopifyLink ? (
            <Link
              to={canonicalUrl}
              onClick={handleProductClick}
              className="mt-2 w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2 px-2 font-mono text-[9px] uppercase tracking-wider hover:from-purple-700 hover:to-indigo-700 transition-all"
              data-testid={`select-options-fav-${group.id}`}
            >
              <ShoppingBag className="w-3 h-3" />
              Select Options
            </Link>
          ) : null
        ) : isAffiliateMode && affiliateLink ? (
          /* Default single region Amazon button - only for affiliate mode */
          group.is_sold_out ? (
            <div
              className="mt-2 w-full flex items-center justify-center gap-2 bg-gray-400 text-white py-2.5 px-4 font-mono text-[10px] uppercase tracking-widest cursor-not-allowed"
              data-testid={`sold-out-btn-${group.id}`}
            >
              Sold Out
            </div>
          ) : (
            <a
              href={affiliateLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 w-full flex items-center justify-center gap-2 bg-site-bg-dark text-site-text-inverse py-2.5 px-4 font-mono text-[10px] uppercase tracking-widest hover:bg-site-bg-dark/90 transition-colors"
              data-testid={`shop-group-${group.id}`}
            >
              {group.shop_button_text || 'Shop On Amazon'}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )
        ) : null}

        {/* Group Tags */}
        <div className="h-10 flex items-center overflow-hidden">
          {group.group_tags?.length > 0 && (
            <div ref={tagsContainerRef} className="flex flex-nowrap gap-1.5 w-full items-center overflow-hidden">
              {group.group_tags.map((tag, idx) => (
                <Link
                  key={idx}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  data-tag
                  className={`text-[10px] font-mono px-2 py-1 bg-site-bg-alt text-site-text-muted rounded hover:bg-stone-200 hover:text-site-text transition-colors whitespace-nowrap flex-shrink-0 ${idx >= visibleTagCount ? 'hidden' : ''}`}
                  data-testid={`group-tag-link-${group.id}-${idx}`}
                >
                  {tag}
                </Link>
              ))}
              {/* +X badge for hidden tags */}
              {group.group_tags.length > visibleTagCount && (
                <span className="text-[10px] font-mono px-2 py-1 bg-site-bg-alt text-site-text-muted rounded whitespace-nowrap flex-shrink-0">
                  +{group.group_tags.length - visibleTagCount}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductGroupCard;
